type AssetClassOption = {
  key?: string;
  value: string;
};

type AssetClassSection = {
  key: string;
  value: string;
  items: {
    value: string;
    label: string;
  }[];
};

export const C3_ASSET_CLASS_RELATIONSHIPS = {
  cash: ["cash", "monetary_fund"],
  diversified_investments: ["multi_asset_fund", "convertible_bond_fund"],
  fixed_income: [
    "short_term_fixed_income_fund",
    "ig_fixed_income_fund",
    "dynamic_european_fixed_income_fund",
    "dynamic_fixed_income_fund",
    "inflation_fund",
    "structured_product",
  ],
  private_debt: ["private_debt"],
  equity: [
    "european_stock_fund",
    "emerging_stock_fund",
    "japan_stock_fund",
    "world_stock_fund",
  ],
  private_equity: ["private_equity_fund"],
  real_estate: ["real_estate"],
} as const;

function normalizeAssetClassValue(
  value: string | null | undefined,
  options: AssetClassOption[],
): string | null {
  if (!value) return null;

  const matchedOption = options.find(
    (option) => option.key === value || option.value === value,
  );

  return matchedOption?.key ?? value;
}

export function getAllowedAssetClass2Options(
  assetClass1Value: string | null | undefined,
  assetClass1Options: AssetClassOption[],
  assetClass2Options: AssetClassOption[],
): AssetClassOption[] {
  const assetClass1Key = normalizeAssetClassValue(
    assetClass1Value,
    assetClass1Options,
  );

  if (!assetClass1Key) return [];

  const allowedAssetClass2Keys: readonly string[] =
    C3_ASSET_CLASS_RELATIONSHIPS[
      assetClass1Key as keyof typeof C3_ASSET_CLASS_RELATIONSHIPS
    ] ?? [];

  return assetClass2Options.filter((option) =>
    option.key ? allowedAssetClass2Keys.includes(option.key) : false,
  );
}

export function isValidAssetClassPair(
  assetClass1Value: string | null | undefined,
  assetClass2Value: string | null | undefined,
  assetClass1Options: AssetClassOption[],
  assetClass2Options: AssetClassOption[],
): boolean {
  if (!assetClass2Value) return true;

  const assetClass1Key = normalizeAssetClassValue(
    assetClass1Value,
    assetClass1Options,
  );
  const assetClass2Key = normalizeAssetClassValue(
    assetClass2Value,
    assetClass2Options,
  );

  if (!assetClass1Key || !assetClass2Key) return false;

  const allowedAssetClass2Keys: readonly string[] =
    C3_ASSET_CLASS_RELATIONSHIPS[
      assetClass1Key as keyof typeof C3_ASSET_CLASS_RELATIONSHIPS
    ] ?? [];

  return allowedAssetClass2Keys.includes(assetClass2Key);
}

export function getAssetClassSections(
  assetClass1Options: AssetClassOption[],
  assetClass2Options: AssetClassOption[],
): AssetClassSection[] {
  const assetClass1Labels = new Map(
    assetClass1Options
      .filter((option): option is AssetClassOption & { key: string } => Boolean(option.key))
      .map((option) => [option.key, option.value]),
  );

  const assetClass2Labels = new Map(
    assetClass2Options
      .filter((option): option is AssetClassOption & { key: string } => Boolean(option.key))
      .map((option) => [option.key, option.value]),
  );

  return Object.entries(C3_ASSET_CLASS_RELATIONSHIPS).map(
    ([assetClass1Key, assetClass2Keys]) => ({
      key: assetClass1Key,
      value: assetClass1Labels.get(assetClass1Key) ?? assetClass1Key,
      items: assetClass2Keys.map((assetClass2Key) => ({
        value: assetClass2Key,
        label: assetClass2Labels.get(assetClass2Key) ?? assetClass2Key,
      })),
    }),
  );
}
